import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subscribable } from 'rxjs/Observable';
import { StorageService } from './storage.service';
import { FVEnviroment } from '../models/enviroment';

export class ApiResponse {
  constructor(
    public data?: { count: number, data: Object } | any,
    public error?: any | undefined,
  ) { }
}

@Injectable()
export class ApiService extends StorageService {
  constructor(
    private httpClient: HttpClient,
    @Inject('enviroment') private enviroment: FVEnviroment,
  ) {
    super();
  }

  /**
   * Make GET request to api
   * @param point
   * @param args
   * @param parameters
   * @param options
   */
  get(point: string, args: string[] = [], parameters: Object = {}, options: Object = {}): Promise<any> {
    return this.request('GET', point, parameters, options, undefined, ...args);
  }

  /**
   * Make DELETE request to api
   * @param point
   * @param args
   * @param parameters
   * @param options
   */
  delete(point: string, args: string[] = [], parameters: Object = {}, options: Object = {}): Promise<any> {
    return this.request('DELETE', point, parameters, options, undefined, ...args);
  }

  /**
   * Make OPTIONS request to api
   * @param point
   * @param args
   * @param parameters
   * @param options
   */
  options(point: string, args: string[] = [], parameters: Object = {}, options: Object = {}): Promise<any> {
    return this.request('OPTIONS', point, parameters, options, undefined, ...args);
  }

  /**
   * Make POST request to api
   * @param point
   * @param args
   * @param parameters
   * @param options
   */
  post(point: string, args: string[] = [], parameters: Object = {}, body: object | string = {}, options: Object = {}): Promise<any> {
    return this.request('POST', point, parameters, options, body, ...args);
  }

  /**
   * Make PATCH request to api
   * @param point
   * @param args
   * @param parameters
   * @param options
   */
  patch(point: string, args: string[] = [], parameters: Object = {}, body: object | string = {}, options: Object = {}): Promise<any> {
    return this.request('PATCH', point, parameters, options, body, ...args);
  }

  /**
   * Make PUT request to api
   * @param point
   * @param args
   * @param parameters
   * @param options
   */
  put(point: string, args: string[] = [], parameters: Object = {}, body: object | string = {}, options: Object = {}): Promise<any> {
    return this.request('PUT', point, parameters, options, body, ...args);
  }

  /**
   * Return generated url
   * @param point
   * @param args
   * @param parameters
   * @param body
   * @param options
   */
  getURL(point: string, args: string[] = [], parameters: Object = {}, body: object | string = {}, options: Object = {}) {
    return this.request('getURL', point, parameters, options, body, ...args);
  }

  get apiRenameList() {
    return ['Name', 'Title'];
  }

  get API(): string {
    return this.enviroment.apiUrl;
  }

  private rename(origin, target) {
    this.languages.forEach((lang) => {
      const from = `${origin}_${lang}`;
      const to = `${origin}`;
      if (target.hasOwnProperty(from) && !target.hasOwnProperty(to)) {
        target[to] = target[from];
        delete target[from];
      }
    });
  }

  /**
   * Transform subscription to promise
   * For using same behavior throught whole application
   * @param subscription
   */
  private subscribeToPromise(subscription: Subscribable<any>): Promise<any> {
    return new Promise((resolve, reject) => {
      subscription.subscribe(resolve, reject);
    });
  }

  /**
   * Generates final url from api_url + view + query parameters;
   * @param url
   * @param parameters
   * @param args
   */
  private generateUrl(url: string, parameters: Object, ...args): string {
    const endUrl = `${url}${args.length ? `/${args.join('/')}` : ''}`;
    const queryParams = Object.keys(parameters).map((key) => `${key}=${this.sanitize(parameters[key])}`).join('&');

    return `${endUrl}${queryParams ? `?${queryParams}` : ''}`;
  }

  private sanitize(value: any) {
    const exists = value !== undefined && value !== null;
    const isNum = (val) => val.constructor === Number;
    const isString = (val) => val.constructor === String;
    if (!exists) {
      return value;
    }
    if (isString(value)) {
      return value.trim();
    }
    if (isNum(value)) {
      return value;
    }
    return JSON.stringify(value);
  }

  /**
   * If data contains fields like Name_ru/Name_ro/Name_en transform it into Name.
   * @param data
   */
  private resolve(response: any) {
    if (response && response.data && Array.isArray(response.data)) {
      response.data.forEach((item) => {
        this.apiRenameList.forEach((renameOrigin) => {
          this.rename(renameOrigin, item);
        });
      });
    }
    return {
      data: response,
      error: null,
    };
  }

  /**
   * Private Abstract request function
   * @param method
   * @param point
   * @param parameters
   * @param options
   * @param args
   */
  private request(method: string, point: string, parameters: Object, options: Object, body: Object = null, ...args): Promise<any> | any {
    method = method.toLowerCase();
    const url = this.generateUrl(this.API + point, parameters, ...args);
    if (method === 'geturl') {
      return url;
    }
    return new Promise((resolve, reject) => {
      const cache = super.get(url);
      const progress = super.getProgress(url);
      const skipCache = options['skipCache'] || false;

      if (cache && skipCache !== true) {
        // Extract response from already finished request
        return resolve(cache);
      } else if (progress && skipCache !== true) {
        // Extract response from already existing request in progress
        progress.subscribe((response) => {
          super.deleteProgress(url);
          const resolvedData = this.resolve(response);
          return resolve(resolvedData);
        });
      } else {
        let callParameters = [];

        // Add headers
        const auth = JSON.parse(localStorage.getItem('auth'));
        const headers = options['headers'] || {};
        if (auth) {
          headers['Authorization'] = `${auth.tokenType} ${auth.token}`;
        }
        const userContext = JSON.parse(localStorage.getItem('userContext'));
        if (!options['skipRole'] && userContext) {
          headers['x-Current-Role'] = `${userContext.role}`;
        }
        options['headers'] = new HttpHeaders(headers);

        if (method === 'get' || method === 'delete' || method === 'options') {
          callParameters = [url, options];
          // Request not in cache/progress so we set it.
          super.setProgress(url);
        } else if (method === 'patch' || method === 'put' || method === 'post') {
          callParameters = [url, body || {}, options];
        }
        this.subscribeToPromise(this.httpClient[method](...callParameters)).then((rawData) => {
          const resolvedData = this.resolve(rawData);
          if (method === 'get' || method === 'delete' || method === 'options') {
            super.set(url, resolvedData);
            const prog = super.getProgress(url);
            if (prog) {
              prog.emit(resolvedData);
              super.deleteProgress(url);
            }
          }
          return resolve(resolvedData);
        }).catch((e) => {
          resolve({ data: null, error: e });
        });
      }
    });
  }

  private get languages() {
    return ['en', 'ro', 'ru'];
  }
}
